import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter73 — Customer Category is a single dropdown with three values. Property Managers are NOT
// listed here on purpose: they live in the dedicated PM module. Legacy values (hsp/warranty/other/
// property_manager) on existing records are preserved in the DB but not offered as new choices.
const CUSTOMER_CATEGORIES = [
  { value: 'residential', label: 'Residential', hint: 'Default for all normal homeowners.' },
  { value: 'commercial',  label: 'Commercial',  hint: 'Restaurants, offices, churches, warehouses, retail stores, etc. (NOT property managers).' },
  { value: 'warranty_company', label: 'Warranty Company', hint: 'Companies that receive warranty/HSP invoices — e.g. HSP Home Service Plan of Ohio, American Home Shield, Choice Home Warranty.' },
  { value: 'property_management', label: 'Property Management', hint: 'Managed properties (CHN, REM Ohio, etc.). Pick the PM company below — we keep the PM Company → Property → Requester → Bill To hierarchy intact.' },
];

const initial = {
  first_name: '', last_name: '', company_name: '',
  email: '', phone: '',
  address: '',
  billing_address: '',
  billing_same: true,
  customer_type: 'residential',
  claims_email: '',
  payment_terms: '',
  notes: '',
  send_invite: false,
};

/**
 * Shared "create a customer" dialog. Used everywhere — Admin → Customers, Schedule shift modal,
 * BillingCreateSheet. Single workflow = no duplicate bugs between dispatch and customer management.
 *
 * Props:
 *   open / onOpenChange — controlled
 *   onCreated(customer) — called on success with {id, name, email, phone, existing?}
 *   trigger — optional ReactNode wrapped in DialogTrigger (if omitted, parent fully controls open)
 */
const QuickAddCustomerDialog = ({ open, onOpenChange, onCreated, onSelectPmCompany }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  // iter305 — Property Management path: pick a canonical PM company, then hand off to
  // the Schedule PM flow (property search / add + requester) instead of creating a
  // loose standalone customer.
  const [pmCompanies, setPmCompanies] = useState([]);
  const [pmCompanyId, setPmCompanyId] = useState('');
  const isPm = form.customer_type === 'property_management';

  useEffect(() => {
    if (isPm && open && pmCompanies.length === 0) {
      axios.get(`${API}/schedule/pm-companies`, { withCredentials: true })
        .then(({ data }) => setPmCompanies(data.companies || []))
        .catch(() => toast.error('Could not load PM companies'));
    }
  }, [isPm, open, pmCompanies.length]);

  const reset = () => { setForm(initial); setPmCompanyId(''); };

  const submit = async (e) => {
    e?.preventDefault();
    // iter305 — PM: never create a loose customer. Require a company, then route to the
    // Schedule PM property/requester flow via the parent callback.
    if (isPm) {
      if (!pmCompanyId) { toast.error('Select a Property Management company'); return; }
      const co = pmCompanies.find((c) => c.id === pmCompanyId);
      if (onSelectPmCompany) {
        onSelectPmCompany(pmCompanyId, co);
        reset();
        onOpenChange(false);
      } else {
        toast.error('PM scheduling flow is unavailable here');
      }
      return;
    }
    if (!form.first_name.trim() && !form.last_name.trim() && !form.company_name.trim()) {
      toast.error('At least First Name + Last Name, or a Company Name, is required.');
      return;
    }
    if (form.send_invite && !form.email.trim()) {
      toast.error('Email required to send invite');
      return;
    }
    setSaving(true);
    try {
      const billing = form.billing_same ? (form.address || '').trim() : (form.billing_address || '').trim();
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        address: form.address.trim(),
        billing_address: billing,
        customer_type: form.customer_type,
        claims_email: form.customer_type === 'warranty_company' ? (form.claims_email.trim() || null) : null,
        payment_terms: form.customer_type === 'warranty_company' ? form.payment_terms.trim() : '',
        notes: form.notes.trim(),
        send_invite: !!form.send_invite,
      };
      const { data } = await axios.post(`${API}/customers`, payload, { withCredentials: true });
      // iter121 — Surface duplicate dedup as a loud red toast, not a quiet info one.
      // Phone OR email matched an existing customer → backend returned that record
      // instead of creating a new one. Spell out exactly why so the user understands
      // the save didn't fail — they just hit a duplicate guard.
      if (data.existing) {
        toast.error(`Phone or email already in use — selected existing "${data.name}"`, {
          description: 'Nothing new was created. To add a separate record, change the phone or email first.',
          duration: 10000,
          style: {
            background: '#7F1D1D',
            border: '1px solid #DC2626',
            color: '#FFFFFF',
          },
        });
      } else {
        toast.success(`Added ${data.name}`);
      }
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d[0]?.msg : 'Quick add failed'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        data-testid="quick-add-customer-dialog"
        className="bg-[#141414] border-[#262626] text-white max-w-md max-h-[85vh] overflow-y-auto"
        style={{ scrollbarGutter: 'stable' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#D4AF37]" /> Add Customer</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">Single source of truth for new customers — works from Customers, Schedule, and Billing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {!isPm && (<>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white">First Name *</Label>
              <Input data-testid="quick-add-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div>
              <Label className="text-white">Last Name *</Label>
              <Input data-testid="quick-add-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
          </div>
          <div>
            <Label className="text-white">Company Name <span className="text-[10px] text-[#A3A3A3]">(optional)</span></Label>
            <Input data-testid="quick-add-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" placeholder="ABC Property Mgmt" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white">Phone</Label>
              <Input data-testid="quick-add-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
            <div>
              <Label className="text-white">Email</Label>
              <Input data-testid="quick-add-email" type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
            </div>
          </div>
          <div>
            <Label className="text-white">Service Address</Label>
            <Input data-testid="quick-add-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, City, OH" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-white">Billing Address</Label>
              <label className="flex items-center gap-2 text-[11px] text-[#A3A3A3]">
                <Switch
                  data-testid="quick-add-billing-same"
                  checked={form.billing_same}
                  onCheckedChange={(v) => setForm({ ...form, billing_same: v })}
                  className="data-[state=checked]:bg-[#D4AF37]"
                />
                Same as service
              </label>
            </div>
            <Input
              data-testid="quick-add-billing-address"
              value={form.billing_same ? form.address : form.billing_address}
              onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
              placeholder={form.billing_same ? 'Mirrors service address' : 'Different billing address'}
              disabled={form.billing_same}
              className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base disabled:opacity-60"
            />
          </div>
          </>)}
          <div>
            <Label className="text-white">Customer Category</Label>
            <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
              <SelectTrigger data-testid="quick-add-type" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {CUSTOMER_CATEGORIES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-white" data-testid={`quick-add-type-${t.value}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#A3A3A3] mt-1">
              {CUSTOMER_CATEGORIES.find((c) => c.value === form.customer_type)?.hint}
              {form.customer_type === 'residential' && ' Property Managers belong in the PM module, not here.'}
            </p>
          </div>
          {isPm && (
            <div data-testid="quick-add-pm-fields" className="space-y-2 bg-[#0A0A0A] border border-[#3b82f6]/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#93c5fd] font-bold">Property Management Company *</p>
              <Select value={pmCompanyId} onValueChange={setPmCompanyId}>
                <SelectTrigger data-testid="quick-add-pm-company" className="bg-[#141414] border-[#262626] text-white h-11 text-base">
                  <SelectValue placeholder="Select PM company…" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] max-h-72">
                  {pmCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-white" data-testid={`quick-add-pm-company-${c.id}`}>
                      {c.name}{c.property_count ? ` · ${c.property_count} propert${c.property_count === 1 ? 'y' : 'ies'}` : ''}
                    </SelectItem>
                  ))}
                  {pmCompanies.length === 0 && <div className="px-3 py-2 text-xs text-[#737373]">No PM companies found.</div>}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#A3A3A3]">Next you'll pick or add the <strong className="text-white">property / service location</strong> and the <strong className="text-white">requester / job contact</strong> — the PM hierarchy stays intact (no loose customer is created).</p>
            </div>
          )}
          {form.customer_type === 'warranty_company' && (
            <div data-testid="warranty-company-fields" className="space-y-3 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">Warranty Company Billing Details</p>
              <div>
                <Label className="text-white text-xs">Claims Email <span className="text-[#A3A3A3]">(used for warranty invoice delivery)</span></Label>
                <Input
                  data-testid="quick-add-claims-email"
                  type="email"
                  inputMode="email"
                  value={form.claims_email}
                  onChange={(e) => setForm({ ...form, claims_email: e.target.value })}
                  placeholder="claims@warrantyco.com"
                  className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                />
              </div>
              <div>
                <Label className="text-white text-xs">Payment Terms</Label>
                <Select value={form.payment_terms || ''} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
                  <SelectTrigger data-testid="quick-add-payment-terms" className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="NET 15" className="text-white">NET 15</SelectItem>
                    <SelectItem value="NET 30" className="text-white">NET 30</SelectItem>
                    <SelectItem value="NET 45" className="text-white">NET 45</SelectItem>
                    <SelectItem value="NET 60" className="text-white">NET 60</SelectItem>
                    <SelectItem value="DUE ON RECEIPT" className="text-white">Due on Receipt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {!isPm && (<>
          <div>
            <Label className="text-white">Notes</Label>
            <Textarea data-testid="quick-add-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Gate code, pets, parking…" className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
          </div>
          <div className="flex items-center gap-3 py-1">
            <Switch data-testid="quick-add-invite" checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: v })} />
            <span className="text-white text-sm">Send portal invite (email + SMS if phone provided)</span>
          </div>
          </>)}
          <div className="flex gap-2 pt-1">
            <Button data-testid="quick-add-submit" type="submit" disabled={saving} className="flex-1 h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : (isPm ? 'Continue to Properties →' : 'Add Customer')}</Button>
            <Button type="button" onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-11">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddCustomerDialog;
